import React from 'react';
import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';

import { purifyHtml } from '~library/purifyHtml';

export const TOOLTIP_ROOT_ID = 'global-tooltip';

const styles = {
  zIndex: 10000,
  padding: '6px 8px',
  backgroundColor: 'rgba(38, 38, 38, 0.95)',
  boxShadow: '0 6px 16px 0 rgba(0, 0, 0, 0.08),0 3px 6px -4px rgba(0, 0, 0, 0.12),0 9px 28px 8px rgba(0, 0, 0, 0.05)',
  color: 'white',
  borderRadius: '6px',
  fontSize: '14px',
  maxWidth: '250px',
  overflowWrap: 'break-word' as const,
  wordWrap: 'break-word' as const,
  wordBreak: 'break-word' as const,
  fontFamily:
    "-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,'Helvetica Neue',Arial,'Noto Sans',sans-serif,'Apple Color Emoji','Segoe UI Emoji','Segoe UI Symbol','Noto Color Emoji'",
};

// Browser default `<ul>` has padding-left ~40px and top/bottom margins, which
// looks broken inside the compact 250px-wide tooltip. Scope a small reset so
// bulleted tooltip content sits flush with the tooltip's own 8px horizontal
// padding and the bullets stay visible. Pixel values are inlined into a static
// CSS string (outside any component) so Ant Design tokens aren't reachable;
// 16px ≈ `paddingXS` and 4px ≈ `marginXXS` in the default token scale.
const tooltipListResetCss = `
  /* react-tooltip's content wrapper ships with 8px 16px padding; GlobalTooltipRoot
     already sets padding on the tooltip — drop the inner padding to avoid doubling.
     Target the stable .react-tooltip-content-wrapper hook class (always added by
     react-tooltip) rather than the hashed CSS-module class, which a future
     react-tooltip build could rename and silently break this reset. */
  #${TOOLTIP_ROOT_ID} .react-tooltip-content-wrapper {
    padding: 0;
  }
  #${TOOLTIP_ROOT_ID} ul,
  #${TOOLTIP_ROOT_ID} ol {
    margin: 0;
    padding-left: 16px; /* FIXME: no exact token — matches paddingXS */
  }
  #${TOOLTIP_ROOT_ID} li + li {
    margin-top: 4px; /* FIXME: no exact token — matches marginXXS */
  }
`;

// react-tooltip v6 removed the `data-tooltip-html` string API. Anchors still set
// `data-tooltip-html` as our own convention; we read it off the active anchor and
// render it as sanitized HTML at this single injection boundary. Anchors that only
// provide plain `data-tooltip-content` fall through to `content`.
export function renderTooltipContent({
  content,
  activeAnchor,
}: {
  content: React.ReactNode | null;
  activeAnchor: Element | null;
}): React.ReactNode {
  const html = activeAnchor?.getAttribute('data-tooltip-html');
  if (html) return <span dangerouslySetInnerHTML={{ __html: purifyHtml(html) }} />;
  return content;
}

function GlobalTooltipRoot() {
  return (
    <>
      <style>{tooltipListResetCss}</style>
      <Tooltip id={TOOLTIP_ROOT_ID} style={styles} render={renderTooltipContent} />
    </>
  );
}

export default GlobalTooltipRoot;
